/**
 * Tiny WebAudio synth for the gamified ceremonies (pack rip, card flip, raffle
 * reel, win fanfare). Everything is generated at runtime — no audio assets to
 * ship, and every call is triggered by a user gesture so autoplay policies are
 * satisfied.
 */

const STORAGE_KEY = "gaole.sfx";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let enabled = readEnabled();

function readEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY) !== "off";
}

function ensureContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.28;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function tone(opts: {
  freq: number;
  to?: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}) {
  const audio = ensureContext();
  if (!audio || !master || !enabled) return;
  const t0 = audio.currentTime + (opts.delay ?? 0);
  const osc = audio.createOscillator();
  const g = audio.createGain();
  osc.type = opts.type ?? "sine";
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.to != null) osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.to), t0 + opts.duration);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(opts.gain ?? 0.3, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + opts.duration + 0.02);
}

function noise(opts: { duration: number; gain?: number; delay?: number; filter?: number; sweepTo?: number }) {
  const audio = ensureContext();
  if (!audio || !master || !enabled) return;
  const t0 = audio.currentTime + (opts.delay ?? 0);
  const frames = Math.floor(audio.sampleRate * opts.duration);
  const buffer = audio.createBuffer(1, frames, audio.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  }
  const src = audio.createBufferSource();
  src.buffer = buffer;
  const bp = audio.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(opts.filter ?? 2200, t0);
  if (opts.sweepTo != null) bp.frequency.exponentialRampToValueAtTime(opts.sweepTo, t0 + opts.duration);
  bp.Q.value = 0.9;
  const g = audio.createGain();
  g.gain.value = opts.gain ?? 0.25;
  src.connect(bp).connect(g).connect(master);
  src.start(t0);
}

export const sfx = {
  isEnabled: () => enabled,
  setEnabled(next: boolean) {
    enabled = next;
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    if (next) ensureContext();
  },
  toggle() {
    sfx.setEnabled(!enabled);
    if (enabled) sfx.tap();
    return enabled;
  },

  /** Light UI tick — selecting a pack, changing a tab. */
  tap() {
    tone({ freq: 620, to: 880, duration: 0.07, type: "triangle", gain: 0.16 });
  },

  /** Foil tearing open. */
  rip() {
    noise({ duration: 0.55, gain: 0.32, filter: 900, sweepTo: 5200 });
    tone({ freq: 180, to: 60, duration: 0.4, type: "sawtooth", gain: 0.1 });
  },

  /** A single card turning over. */
  flip() {
    noise({ duration: 0.13, gain: 0.16, filter: 3200, sweepTo: 1400 });
    tone({ freq: 420, to: 700, duration: 0.12, type: "triangle", gain: 0.12 });
  },

  /** Card revealed. `intensity` 0-3 scales the fanfare. */
  reveal(intensity: 0 | 1 | 2 | 3 = 0) {
    const roots = [523.25, 587.33, 659.25, 783.99];
    const root = roots[intensity] ?? 523.25;
    tone({ freq: root, duration: 0.2, type: "triangle", gain: 0.22 });
    if (intensity >= 1) tone({ freq: root * 1.25, duration: 0.22, type: "triangle", gain: 0.18, delay: 0.08 });
    if (intensity >= 2) tone({ freq: root * 1.5, duration: 0.26, type: "sine", gain: 0.2, delay: 0.16 });
    if (intensity >= 3) {
      tone({ freq: root * 2, duration: 0.5, type: "sine", gain: 0.24, delay: 0.24 });
      noise({ duration: 0.7, gain: 0.1, filter: 6000, delay: 0.24 });
    }
  },

  /** Repeating tick while the raffle reel spins — call per detent. */
  tick(pitch = 1) {
    tone({ freq: 1200 * pitch, duration: 0.035, type: "square", gain: 0.07 });
  },

  /** Winner fanfare. */
  win() {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      tone({ freq: f, duration: 0.5, type: "triangle", gain: 0.22, delay: i * 0.1 });
    });
    noise({ duration: 1.1, gain: 0.09, filter: 5200, delay: 0.2 });
  },

  /** Low descending tone for near-misses / closing states. */
  lose() {
    tone({ freq: 330, to: 160, duration: 0.5, type: "sine", gain: 0.16 });
  },
};
